import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Chip,
  Grid
} from '@mui/material';
import {
  ViewModule as CardViewIcon,
  BarChart as ChartViewIcon,
  Movie as MovieIcon,
  TrendingUp as RevenueIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { brandColors } from '../../theme/theme';

/**
 * ComparableFilmsComparison Component
 * 
 * Displays comparable films with toggle between card view and chart view.
 * Shows box office performance visualization and success indicators.
 */

const ComparableFilmsComparison = ({ films }) => {
  const [view, setView] = useState('cards'); // 'cards' or 'chart'

  if (!films || films.length === 0) return null;

  // Prepare chart data
  const chartData = films
    .filter(f => f.boxOfficeValue)
    .map(f => ({
      name: f.title.length > 20 ? f.title.substring(0, 20) + '...' : f.title,
      fullName: f.title,
      value: f.boxOfficeValue,
      display: f.boxOffice,
      year: f.year
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header with View Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600 }}>
          Comparable Films ({films.length})
        </Typography>
        
        {chartData.length >= 2 && (
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, newView) => newView && setView(newView)}
            size="small"
          >
            <ToggleButton value="cards">
              <CardViewIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
              Cards
            </ToggleButton>
            <ToggleButton value="chart">
              <ChartViewIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
              Chart
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Card View */}
      {view === 'cards' && (
        <Grid container spacing={2}>
          {films.map((film, index) => (
            <Grid item xs={12} md={6} key={index}>
              <EnhancedFilmCard film={film} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Chart View */}
      {view === 'chart' && chartData.length >= 2 && (
        <Paper
          elevation={1}
          sx={{
            p: 3,
            backgroundColor: '#f9f9f9',
            border: `1px solid ${brandColors.primary}15`
          }}
        >
          <Typography variant="subtitle2" sx={{ color: brandColors.primary, mb: 2, opacity: 0.8 }}>
            Box Office Performance Comparison
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={brandColors.primary + '20'} />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fill: brandColors.primary, fontSize: 12 }}
              />
              <YAxis
                label={{ value: 'Box Office ($M)', angle: -90, position: 'insideLeft', fill: brandColors.primary }}
                tick={{ fill: brandColors.primary }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: brandColors.white,
                  border: `2px solid ${brandColors.secondary}`,
                  borderRadius: 8
                }}
                formatter={(value, name, props) => [`$${value}M`, props.payload.fullName]}
                labelFormatter={(label) => ''}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.value >= 200 ? '#388e3c' : entry.value >= 100 ? '#f57c00' : brandColors.secondary}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          
          {/* Legend */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Chip label="$200M+ Success" size="small" sx={{ backgroundColor: '#388e3c', color: 'white' }} />
            <Chip label="$100-200M Solid" size="small" sx={{ backgroundColor: '#f57c00', color: 'white' }} />
            <Chip label="<$100M Moderate" size="small" sx={{ backgroundColor: brandColors.secondary, color: 'white' }} />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

// Enhanced Film Card Component
const EnhancedFilmCard = ({ film }) => {
  const getSuccessColor = (boxOfficeValue) => {
    if (!boxOfficeValue) return brandColors.primary;
    if (boxOfficeValue >= 200) return '#388e3c';
    if (boxOfficeValue >= 100) return '#f57c00';
    return brandColors.secondary;
  };

  const successColor = getSuccessColor(film.boxOfficeValue);

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        borderLeft: `4px solid ${successColor}`,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <MovieIcon sx={{ color: successColor, mr: 1, mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: brandColors.primary, fontWeight: 600, lineHeight: 1.3 }}>
              {film.title}
            </Typography>
            {film.year && (
              <Typography variant="caption" sx={{ color: brandColors.primary, opacity: 0.6 }}>
                {film.year}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Box Office Badge */}
        {film.boxOffice && (
          <Chip
            label={`Box Office: ${film.boxOffice}`}
            size="small"
            icon={<RevenueIcon />}
            sx={{
              mb: 1.5,
              backgroundColor: `${successColor}20`,
              color: successColor,
              fontWeight: 600,
              '& .MuiChip-icon': { color: successColor }
            }}
          />
        )}

        {/* Connection */}
        {film.connection && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: brandColors.secondary,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.7rem'
              }}
            >
              Connection
            </Typography>
            <Typography variant="body2" sx={{ color: brandColors.primary, opacity: 0.85, lineHeight: 1.6, mt: 0.5 }}>
              {film.connection}
            </Typography>
          </Box>
        )}

        {/* Learning */}
        {film.learning && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: brandColors.secondary,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.7rem'
              }}
            >
              Key Learning
            </Typography>
            <Typography variant="body2" sx={{ color: brandColors.primary, opacity: 0.85, lineHeight: 1.6, mt: 0.5 }}>
              {film.learning}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ComparableFilmsComparison;

